import { useState, useEffect } from "react";
import BottomSheet from "../../../../components/common/bottom-sheet";
import { useCreateWithdrawalRequest, useUserWallet } from "../hooks/use-wallet";
import { calculateMaxWithdrawable } from "../utils/wallet-utils";
import { notify } from "../../../../utils/toast";

const bankList = [
  "국민은행",
  "우리은행",
  "신한은행",
  "기업은행",
  "하나은행",
  "농협은행",
  "지역농축협",
  "SC은행",
  "한국씨티은행",
  "우체국",
  "카카오뱅크",
  "토스뱅크",
  "케이뱅크",
  "경남은행",
  "광주은행",
  "IM[구 대구은행]",
  "부산은행",
  "산림조합",
  "산업은행",
  "저축은행",
  "새마을금고",
  "수협",
  "신협",
  "전북은행",
  "제주은행",
];

interface WithdrawalRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const WithdrawalRequestModal: React.FC<WithdrawalRequestModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { wallet, refetch: refetchWallet } = useUserWallet();
  const { createWithdrawalRequest, loading } = useCreateWithdrawalRequest();

  const [step, setStep] = useState<1 | 2>(1); // 1: 금액 입력, 2: 계좌 정보 입력
  const [amount, setAmount] = useState<number>(0);
  const [bank, setBank] = useState<string>("");
  const [accountNumber, setAccountNumber] = useState<string>("");
  const [accountHolder, setAccountHolder] = useState<string>("");
  const [errors, setErrors] = useState<{
    amount?: string;
    bank?: string;
    accountNumber?: string;
    accountHolder?: string;
  }>({});

  const availableBalance = wallet?.availableBalance || 0;
  const maxWithdrawable = calculateMaxWithdrawable(availableBalance);

  // 최대 출금 가능 금액으로 초기화
  useEffect(() => {
    if (isOpen) {
      setStep(1); // 모달 열릴 때 1단계로 초기화
      if (maxWithdrawable > 0) {
        setAmount(maxWithdrawable);
      } else {
        setAmount(0);
      }
    }
  }, [isOpen, maxWithdrawable]);

  const validateAmount = (): boolean => {
    const newErrors: typeof errors = {};

    if (amount < 1000) {
      newErrors.amount = "출금 금액은 최소 1,000원 이상이어야 합니다.";
    } else if (amount % 1000 !== 0) {
      newErrors.amount = "출금 금액은 1,000원 단위로만 가능합니다.";
    } else if (amount > maxWithdrawable) {
      newErrors.amount = `최대 출금 가능 금액은 ${maxWithdrawable.toLocaleString()}원입니다.`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAccount = (): boolean => {
    const newErrors: typeof errors = {};

    if (!bank) {
      newErrors.bank = "은행을 선택해주세요.";
    }

    if (!accountNumber.trim()) {
      newErrors.accountNumber = "계좌번호를 입력해주세요.";
    }

    if (!accountHolder.trim()) {
      newErrors.accountHolder = "예금주명을 입력해주세요.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateAmount()) {
      setStep(2);
    }
  };

  const handleBackStep = () => {
    setStep(1);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateAccount()) {
      return;
    }

    try {
      await createWithdrawalRequest({
        variables: {
          input: {
            amount,
            bank: bank,
            accountNumber: accountNumber.trim(),
            accountHolder: accountHolder.trim(),
          },
        },
      });

      notify("출금 요청이 완료되었습니다.");
      await refetchWallet();
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error("Error creating withdrawal request:", error);
      notify(
        error.message || "출금 요청 중 오류가 발생했습니다. 다시 시도해주세요."
      );
    }
  };

  const handleClose = () => {
    setStep(1);
    setAmount(0);
    setBank("");
    setAccountNumber("");
    setAccountHolder("");
    setErrors({});
    onClose();
  };

  const handleAmountChange = (value: number) => {
    // 1000원 단위로만 입력 가능
    const rounded = Math.floor(value / 1000) * 1000;
    const finalAmount = Math.max(0, Math.min(rounded, maxWithdrawable));
    setAmount(finalAmount);
    if (errors.amount) {
      setErrors({ ...errors, amount: undefined });
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={handleClose}>
      <div className="bg-white rounded-t-3xl p-6 pb-8 max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-6">
          {step === 1 ? (
            <h2 className="text-xl font-bold">출금 금액 입력</h2>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleBackStep}
                className="text-gray-500 hover:text-gray-700"
              >
                ←
              </button>
              <h2 className="text-xl font-bold">계좌 정보 입력</h2>
            </div>
          )}
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        {step === 1 ? (
          /* 1단계: 출금 금액 입력 */
          <>
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">
                출금 금액
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => handleAmountChange(Number(e.target.value))}
                  min={1000}
                  max={maxWithdrawable}
                  step={1000}
                  className="flex-1 border border-gray-300 rounded-lg px-4 py-3 text-lg"
                  placeholder="출금 금액을 입력하세요"
                />
                <span className="text-gray-600">원</span>
              </div>
              {errors.amount && (
                <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
              )}
              <p className="text-gray-500 text-sm mt-2">
                최대 출금 가능 금액: {maxWithdrawable.toLocaleString()}원
              </p>
              <p className="text-gray-400 text-xs mt-1">
                출금은 1,000원 단위로만 가능해요
              </p>
            </div>

            {/* 출금 정책 안내 */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">
                운영팀 확인 후 순차적으로 입금돼요
              </p>
              <p className="text-sm text-gray-600">
                보통 1~3영업일 정도 소요돼요
              </p>
            </div>

            {/* 버튼 */}
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 border border-gray-300 rounded-lg py-3 font-semibold text-gray-700"
              >
                취소
              </button>
              <button
                onClick={handleNextStep}
                disabled={amount < 1000 || amount % 1000 !== 0}
                className="flex-1 bg-[#1692fc] text-white rounded-lg py-3 font-semibold hover:bg-[#1562fc] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                다음
              </button>
            </div>
          </>
        ) : (
          /* 2단계: 계좌 정보 입력 */
          <>
            {/* 선택된 출금 금액 표시 */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">출금 금액</p>
              <p className="text-2xl font-bold text-blue-600">
                {amount.toLocaleString()}원
              </p>
            </div>

            {/* 계좌 정보 입력 */}
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">
                은행명
              </label>
              <select
                value={bank}
                onChange={(e) => {
                  setBank(e.target.value);
                  if (errors.bank) {
                    setErrors({ ...errors, bank: undefined });
                  }
                }}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 bg-white"
              >
                <option value="">은행을 선택하세요</option>
                {bankList.map((bankName) => (
                  <option key={bankName} value={bankName}>
                    {bankName}
                  </option>
                ))}
              </select>
              {errors.bank && (
                <p className="text-red-500 text-sm mt-1">{errors.bank}</p>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">
                계좌번호
              </label>
              <input
                type="text"
                value={accountNumber}
                onChange={(e) => {
                  setAccountNumber(e.target.value);
                  if (errors.accountNumber) {
                    setErrors({ ...errors, accountNumber: undefined });
                  }
                }}
                className="w-full border border-gray-300 rounded-lg px-4 py-3"
                placeholder="계좌번호를 입력하세요"
              />
              {errors.accountNumber && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.accountNumber}
                </p>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">
                예금주명
              </label>
              <input
                type="text"
                value={accountHolder}
                onChange={(e) => {
                  setAccountHolder(e.target.value);
                  if (errors.accountHolder) {
                    setErrors({ ...errors, accountHolder: undefined });
                  }
                }}
                className="w-full border border-gray-300 rounded-lg px-4 py-3"
                placeholder="예금주명을 입력하세요"
              />
              {errors.accountHolder && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.accountHolder}
                </p>
              )}
            </div>

            {/* 주의 메시지 */}
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 font-medium">
                ⚠️ 주의사항
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                계좌 정보를 잘못 입력하여 발생하는 불이익에 대해서는 책임지지 않습니다.
              </p>
            </div>

            {/* 버튼 */}
            <div className="flex gap-3">
              <button
                onClick={handleBackStep}
                className="flex-1 border border-gray-300 rounded-lg py-3 font-semibold text-gray-700"
              >
                이전
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-[#1692fc] text-white rounded-lg py-3 font-semibold hover:bg-[#1562fc] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "처리 중..." : "출금 요청하기"}
              </button>
            </div>
          </>
        )}
      </div>
    </BottomSheet>
  );
};

export default WithdrawalRequestModal;
